import React, { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

import { cn } from "@/utils/cn";

import Checkbox from "../checkbox/checkbox";
import HStack from "../hstack/hstack";
import Stack from "../stack/stack";
import Text from "../text/text";

interface FormGroupProps {
  className?: string;
  /** Input label message. */
  labelText?: React.ReactNode;
  /** Input label tooltip message. */
  labelTitle?: string;
  /** Class name to override the label */
  labelClassName?: string;
  /** Input hint message. */
  hintText?: React.ReactNode;
  /** Input errors. */
  errors?: string[];
  /** Optional className for errors text */
  errorsClassName?: string;
  /** Adds an asterisk to the label (only used for Marketing pages) */
  isRequired?: boolean;
  /** Elements to display within the FormGroup. */
  children: React.ReactNode;
}

/** Input container with label. Renders the child. */
const FormGroup: React.FC<FormGroupProps> = (props) => {
  const {
    className,
    children,
    errors = [],
    errorsClassName,
    labelText,
    labelClassName,
    labelTitle,
    hintText,
    isRequired,
  } = props;
  const formFieldId: string = useMemo(() => `field-${uuidv4()}`, []);
  const inputChildren = React.Children.toArray(children);
  const hasError = errors?.length > 0;

  let firstChild;
  if (React.isValidElement(inputChildren[0])) {
    firstChild = React.cloneElement(
      inputChildren[0],
      // @ts-ignore
      { id: formFieldId },
    );
  }

  // @ts-ignore
  const isCheckboxFormGroup = firstChild?.type === Checkbox;

  if (isCheckboxFormGroup) {
    return (
      <HStack alignItems="start" space={2} className={className}>
        {firstChild}

        <Stack>
          {labelText && (
            <label
              htmlFor={formFieldId}
              data-testid="form-group-label"
              className={cn(
                "-mt-0.5 block text-sm font-medium text-gray-900 dark:text-gray-100",
                labelClassName,
              )}
              title={labelTitle}
            >
              {labelText}
            </label>
          )}

          {hasError && (
            <div>
              <Text
                data-testid="form-group-error"
                theme="danger"
                weight="medium"
                size="xs"
                className={cn("mt-1", errorsClassName)}
              >
                {errors.join(", ")}
              </Text>
            </div>
          )}

          {hintText && (
            <p
              data-testid="form-group-hint"
              className="mt-0.5 text-xs text-muted-foreground"
            >
              {hintText}
            </p>
          )}
        </Stack>
      </HStack>
    );
  }

  return (
    <div className={className}>
      {labelText && (
        <label
          htmlFor={formFieldId}
          data-testid="form-group-label"
          className={cn(
            "block text-sm font-medium text-gray-900 dark:text-gray-100",
            labelClassName,
          )}
          title={labelTitle}
        >
          {labelText}

          {isRequired && <span className="ml-1 text-primary-500">*</span>}
        </label>
      )}

      <div className="mt-1 dark:text-white">
        {hintText && (
          <p
            data-testid="form-group-hint"
            className="mb-0.5 text-xs text-muted-foreground"
          >
            {hintText}
          </p>
        )}

        {firstChild}
        {inputChildren.filter((_, i) => i !== 0)}

        {hasError && (
          <Text
            data-testid="form-group-error"
            theme="danger"
            weight="medium"
            size="xs"
            className={cn("mt-1", errorsClassName)}
          >
            {errors.join(", ")}
          </Text>
        )}
      </div>
    </div>
  );
};

export default FormGroup;
